import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Car, Navigation, Clock, MapPin } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import Breadcrumb from '../components/Breadcrumb';
import TaxiStandCard from '../components/TaxiStandCard';
import NearbyTaxiList from '../components/NearbyTaxiList';
import { getTaxiStands } from '../services/locationService';
import { createSlug, formatTitle } from '../utils/url';
import { useGeolocation } from '../hooks/useGeolocation';
import { useNearbyTaxis } from '../hooks/useNearbyTaxis';

export default function TaxiStands() {
  const { citySlug, districtSlug, neighborhoodSlug } = useParams();
  const navigate = useNavigate();
  const [taxiStands, setTaxiStands] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showNearbyTaxis, setShowNearbyTaxis] = useState(false);
  const [isNearbyListExpanded, setIsNearbyListExpanded] = useState(true);

  const { location, loading: locationLoading, getLocation } = useGeolocation();
  const { places, loading: placesLoading, error: placesError, searchNearbyTaxis } = useNearbyTaxis();

  useEffect(() => {
    if (citySlug && districtSlug && neighborhoodSlug) {
      const cleanNeighborhoodSlug = neighborhoodSlug.replace(/-taksi$/, '');
      loadTaxiStands(citySlug, districtSlug, cleanNeighborhoodSlug);
    }
  }, [citySlug, districtSlug, neighborhoodSlug]);

  const loadTaxiStands = async (citySlug: string, districtSlug: string, neighborhoodSlug: string) => {
    try {
      setLoading(true);
      const data = await getTaxiStands(citySlug, districtSlug, neighborhoodSlug);
      
      // Eğer mahallede hiç taksi durağı yoksa 404'e yönlendir
      if (!data || data.length === 0) {
        // HTTP status code meta tag'ini ekle
        const meta = document.createElement('meta');
        meta.httpEquiv = 'status';
        meta.content = '404';
        document.head.appendChild(meta);
        
        // SEO için title güncelle
        document.title = 'Taksi Durağı Bulunamadı - En Yakın Taksi';
        
        // NotFound component'ine yönlendir
        navigate('/404', { replace: true });
        return;
      }
      
      setTaxiStands(data);
    } catch (err) {
      setError('Taksi durakları yüklenirken bir hata oluştu.');
      console.error('Error loading taxi stands:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFindNearbyTaxis = async () => {
    try {
      const newLocation = await getLocation();
      if (newLocation && !newLocation.error) {
        await searchNearbyTaxis({ 
          lat: newLocation.latitude, 
          lng: newLocation.longitude 
        });
        setShowNearbyTaxis(true);
        setIsNearbyListExpanded(true);
      }
    } catch (err) {
      console.error('Konum alınamadı:', err);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-800 rounded-lg w-1/4"></div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-40 bg-gray-800 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const neighborhood = taxiStands[0]?.neighborhood;
  const district = neighborhood?.district;
  const city = district?.city;

  const formattedCityName = formatTitle(city?.name || citySlug?.replace(/-/g, ' ') || '');
  const formattedDistrictName = formatTitle(district?.name || districtSlug?.replace(/-/g, ' ') || '');
  const formattedNeighborhoodName = formatTitle(neighborhood?.name || neighborhoodSlug?.replace(/-taksi$/, '').replace(/-/g, ' ') || '');

  return (
    <div className="container mx-auto px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-4 sm:mb-6">
          <Breadcrumb items={[
            { name: formattedCityName, path: `/il/${createSlug(formattedCityName)}` },
            { name: formattedDistrictName, path: `/ilce/${createSlug(formattedCityName)}/${createSlug(formattedDistrictName)}` },
            { name: `${formattedNeighborhoodName} Taksi`, path: `/mahalle/${createSlug(formattedCityName)}/${createSlug(formattedDistrictName)}/${createSlug(formattedNeighborhoodName, 'taksi')}` }
          ]} />
        </div>
        
        <div className="bg-gray-800/40 backdrop-blur-xl rounded-3xl border border-white/5 shadow-2xl overflow-hidden">
          <div className="p-4 sm:p-8">
            {/* Header Section */}
            <div className="flex flex-col gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl shadow-lg shadow-yellow-400/20">
                  <Car className="w-8 h-8 text-gray-900" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-white">
                    {formattedNeighborhoodName} Taksi Durakları
                  </h1>
                  <p className="text-gray-400 mt-1">
                    {formattedDistrictName}, {formattedCityName}
                  </p>
                </div>
              </div>

              <SearchBar 
                placeholder="Taksi durağı ara..."
                onChange={setSearch}
              />

              {/* Yakındaki Taksileri Bul Butonu */}
              <button
                onClick={handleFindNearbyTaxis}
                disabled={locationLoading || placesLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-yellow-400/20 hover:shadow-yellow-400/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {locationLoading || placesLoading ? (
                  <>
                    <Clock className="w-5 h-5 animate-spin" />
                    <span className="text-base sm:text-lg">Konum Alınıyor...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="w-5 h-5 animate-bounce-subtle" />
                    <span className="text-base sm:text-lg">{formattedNeighborhoodName} En Yakın Taksileri Bul</span>
                  </>
                )}
              </button>
            </div>

            {/* Yakındaki Taksiler Listesi */}
            {showNearbyTaxis && (
              <div className="mb-6 sm:mb-8">
                <NearbyTaxiList
                  places={places}
                  loading={placesLoading}
                  error={placesError}
                  neighborhoodName={formattedNeighborhoodName}
                  isExpanded={isNearbyListExpanded}
                  onToggle={() => setIsNearbyListExpanded(!isNearbyListExpanded)}
                />
              </div>
            )}

            {/* Taksi Durakları Listesi */}
            <div className="space-y-4">
              {taxiStands
                .filter(stand => 
                  stand.name.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR')) ||
                  stand.address.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR'))
                )
                .map(stand => (
                  <TaxiStandCard key={stand.id} stand={stand} />
                ))}

              {taxiStands.filter(stand => 
                stand.name.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR')) ||
                stand.address.toLocaleLowerCase('tr-TR').includes(search.toLocaleLowerCase('tr-TR'))
              ).length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <p className="text-gray-400">Aradığınız kriterlere uygun taksi durağı bulunamadı.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}